import { useCallback } from 'react';

import { EMPTY_ARRAY, EMPTY_OBJECT } from '@e-socna/empty';
import type { ContentOriginId } from '@e-socna/data-layer';

import { SpecialSectionForm } from '../../forms/SpecialSection';
import { FormVariant } from '../../forms';
import { CompatibleSectionTemplatesForm } from '../../forms/CompatibleSectionTemplates';
import { useActionBlocker } from '../../hooks/useActionBlocker.hook';
import type { CompatibleSectionTemplates_SectionTemplates } from '../../forms/CompatibleSectionTemplates/hooks/useFindAllCompatibleSectionTemplatesQuery.hook';

import { RightDrawer } from '../RightDrawer';
import { Tabs, TabsStrategy } from '../Tabs';

import { useDocumentEditorContext } from './contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import { DocumentEditorTwoStepContentActionType } from './enums/two-step-content-action-type.enum';
import type { TextualContent } from './types/textual-content.type';
import type { Editable } from './types/editable.type';

export const EditionDrawer = () => {
  const {
    state,
    actions,
    props: {
      getters: {
        storage: getStorage,
        contentConstraints: getContentConstraints,
      },
    },
  } = useDocumentEditorContext<Editable>();

  const [closeDrawer, setMustBlockAction] = useActionBlocker(
    actions.closeDrawer,
  );

  const handleSubmit = useCallback(
    (
      content:
        TextualContent | readonly CompatibleSectionTemplates_SectionTemplates[],
      contentOriginId: ContentOriginId,
    ) => {
      actions.fulfillTwoStepContentAction({
        content,
        contentOriginId,
      });
    },
    [actions],
  );

  const compatibleSectionTypeId = getContentConstraints?.(
    state.editable,
  ).compatibleSectionTypeId;

  return (
    <RightDrawer isOpen={state.isDrawerOpen} onDrawerClose={closeDrawer}>
      <Tabs
        id="document-editor-drawer-tabs"
        strategy={TabsStrategy.Stateful}
        sx={{ height: '100%' }}
        tabs={[
          {
            key: 'specialSection',
            label: 'Contenu Spécial',
            panel: (
              <SpecialSectionForm
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(state.currentTwoStepContentAction
                  ?.twoStepContentActionType ===
                DocumentEditorTwoStepContentActionType.Update
                  ? {
                      initialValues:
                        state.currentTwoStepContentAction.textualContent,
                      formVariant: FormVariant.Modification,
                    }
                  : { formVariant: FormVariant.Creation })}
                onCancel={closeDrawer}
                onSubmit={handleSubmit}
                orientation={state.currentTwoStepContentAction?.orientation}
                paperFormat={state.currentTwoStepContentAction?.paperFormat}
                setMustBlockAction={setMustBlockAction}
                storage={getStorage(state.editable)}
              />
            ),
            value: 'specialSection',
          },
          ...(state.currentTwoStepContentAction?.twoStepContentActionType !==
          DocumentEditorTwoStepContentActionType.Update
            ? [
                {
                  key: 'catalogSection',
                  label: 'Sections Type',
                  panel: (
                    <CompatibleSectionTemplatesForm
                      alreadyUsedIds={
                        state.currentTwoStepContentAction?.alreadyUsedIds
                      }
                      formVariant={FormVariant.Creation}
                      onCancel={closeDrawer}
                      onSubmit={handleSubmit}
                      onlyOneChoice={
                        state.currentTwoStepContentAction?.onlyOneChoice
                      }
                      setMustBlockAction={setMustBlockAction}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...(compatibleSectionTypeId
                        ? { typeId: compatibleSectionTypeId }
                        : EMPTY_OBJECT)}
                    />
                  ),
                  value: 'catalogSection',
                },
              ]
            : EMPTY_ARRAY),
        ]}
        title="Modes d'insertion d'une nouvelle section"
      />
    </RightDrawer>
  );
};
